import React, { useState } from 'react';
import { css } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import ScoutAvatar from '@birdi/icons/ScoutAvatar.svg';
import { BetaBadge } from '@birdi/components-common';
import SendGradient from '@birdi/icons/send-gradient.svg';
import { useDispatch } from 'react-redux';
import { useSnapshot } from 'valtio';
import { openAssistant } from './reducer';
import { assistantUi } from './map-bridge';

// "Popular questions" shortcuts shown under the prompt.
const SUGGESTIONS = [
  'Create a new map',
  'Process my imagery',
  'Measure a volume',
  'Generate a report',
];

/**
 * The dashboard hero: a greeting + prompt band that is the first thing a user
 * sees after login. Submitting hands the prompt to the docked assistant.
 */
export const AssistantHero: React.FC = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState('');
  // Plan gate — mirrored from meta by AssistantWidget (see map-bridge).
  const { enabled } = useSnapshot(assistantUi);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    dispatch(openAssistant(trimmed));
    setValue('');
  };

  if (!enabled) return null;

  return (
    <div
      data-testid="assistant-hero"
      css={(theme: BirdiTheme) => css`
        /* Sand border at rest; canary on focus — same outline as the other
           Scout entry points (AssistantLauncher's side tab, the widget's
           composer). */
        background: ${theme.mono0};
        border: 2px solid ${theme.sand};
        &:focus-within {
          border-color: ${theme.canary};
        }
        border-radius: 16px;
        margin-bottom: 0.75em;
        padding: 1.25em 1.5em 1.35em;
        display: flex;
        flex-direction: column;
        @media (max-width: ${breakpoints.s}) {
          padding: 1em 1em 1.1em;
        }
        /* The assistant is disabled on phones — hide its entry points too. */
        @media (max-width: ${breakpoints.xs}) {
          display: none;
        }
      `}
    >
      <h2
        css={(theme: BirdiTheme) => css`
          margin: 0 0 0.85em;
          font-weight: 700;
          color: ${theme.midnight};
          font-size: 1.25em;
          @media (max-width: ${breakpoints.s}) {
            font-size: 1.1em;
          }
        `}
      >
        Welcome back! What would you like to do today?{' '}
        <BetaBadge />
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
        css={css`
          width: 100%;
        `}
      >
        <div
          css={(theme: BirdiTheme) => css`
            display: flex;
            align-items: center;
            gap: 10px;
            border: 1px solid ${theme.mono30};
            border-radius: 12px;
            padding: 8px 10px 8px 14px;
          `}
        >
          <span
            aria-hidden
            css={css`
              display: flex;
              flex-shrink: 0;
              svg {
                width: 24px;
                height: 24px;
                border-radius: 50%;
              }
            `}
          >
            <ScoutAvatar />
          </span>
          <input
            data-testid="assistant-hero-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Ask anything or pick a suggestion below. E.g. 'I want to create a new map' or 'upload a new data set'"
            css={(theme: BirdiTheme) => css`
              flex: 1;
              min-width: 0;
              border: none;
              outline: none;
              font-size: 0.95em;
              font-family: inherit;
              color: ${theme.mono70};
              background: transparent;
              padding: 6px 0;
              &::placeholder {
                color: ${theme.mono50};
              }
            `}
          />
          <button
            type="submit"
            aria-label="Ask Birdi's AI Agent"
            disabled={!value.trim()}
            css={(theme: BirdiTheme) => css`
              border: none;
              background: transparent;
              cursor: pointer;
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 6px;
              transition: opacity 0.15s ease;
              &:disabled {
                opacity: 0.4;
                cursor: default;
              }
              svg {
                width: 24px;
                height: 24px;
              }
            `}
          >
            <SendGradient />
          </button>
        </div>
      </form>

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          margin-top: 0.9em;
        `}
      >
        <span
          css={(theme: BirdiTheme) => css`
            font-size: 0.82em;
            color: ${theme.mono50};
            margin-right: 2px;
          `}
        >
          Popular questions
        </span>
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => submit(s)}
            css={(theme: BirdiTheme) => css`
              border: none;
              background: ${theme.lightOcean};
              color: ${theme.midnight};
              border-radius: 999px;
              padding: 7px 14px;
              font-size: 0.85em;
              font-family: inherit;
              cursor: pointer;
              transition: all 0.15s ease;
              &:hover {
                color: ${theme.ocean};
              }
            `}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};
